import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Plus, Trash2, Upload, GripVertical, Image as ImageIcon,
  Layout, Eye, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyButton = () => ({ label: '', href: '' });

const LAYOUTS = [
  { value: 'centered', label: 'Centered' },
  { value: 'left', label: 'Left-aligned' },
];

export default function AdminHeroEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/admin/dashboard';

  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user && !['super_admin', 'admin'].includes(user.role)) {
      toast.error('Access Denied: Admin only');
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hero-content`, { headers: authHeaders() });
      const items = res.data?.items || [];
      setHeroes(items);
      if (!activeSlug && items.length) {
        setActiveSlug(items[0].slug);
        setDraft({ ...items[0] });
      }
    } catch (e) {
      toast.error('Failed to load heroes');
    } finally {
      setLoading(false);
    }
  };

  const selectHero = (slug) => {
    const h = heroes.find((x) => x.slug === slug);
    if (!h) return;
    setActiveSlug(slug);
    setDraft({ ...h });
  };

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  // Buttons -------------------------------------------------------------
  const addButton = () =>
    setDraft((d) => ({ ...d, buttons: [...(d.buttons || []), emptyButton()] }));
  const updateButton = (i, field, value) =>
    setDraft((d) => {
      const next = [...(d.buttons || [])];
      next[i] = { ...next[i], [field]: value };
      return { ...d, buttons: next };
    });
  const removeButton = (i) =>
    setDraft((d) => ({ ...d, buttons: (d.buttons || []).filter((_, idx) => idx !== i) }));

  // Pills ---------------------------------------------------------------
  const addPill = () => setDraft((d) => ({ ...d, pills: [...(d.pills || []), ''] }));
  const updatePill = (i, value) =>
    setDraft((d) => {
      const next = [...(d.pills || [])];
      next[i] = value;
      return { ...d, pills: next };
    });
  const removePill = (i) =>
    setDraft((d) => ({ ...d, pills: (d.pills || []).filter((_, idx) => idx !== i) }));

  // Slides --------------------------------------------------------------
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const uploadSlide = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeSlug) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(
        `${API}/hero-content/${activeSlug}/slides/upload`,
        fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      const url = up.data?.url;
      setDraft((d) => ({
        ...d,
        slides: [...(d.slides || []), { image: url, caption: '' }],
      }));
      toast.success('Slide added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const replaceSlide = async (i, file) => {
    if (!activeSlug) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(
        `${API}/hero-content/${activeSlug}/slides/upload`,
        fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      const url = up.data?.url;
      setDraft((d) => {
        const next = [...(d.slides || [])];
        next[i] = { ...next[i], image: url };
        return { ...d, slides: next };
      });
      toast.success('Image replaced — remember to Save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    } finally {
      setUploading(false);
    }
  };

  const updateSlideCaption = (i, caption) =>
    setDraft((d) => {
      const next = [...(d.slides || [])];
      next[i] = { ...next[i], caption };
      return { ...d, slides: next };
    });

  const removeSlide = (i) =>
    setDraft((d) => ({ ...d, slides: (d.slides || []).filter((_, idx) => idx !== i) }));

  const handleSlidesDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((d) => {
      const items = d.slides || [];
      const oldIndex = items.findIndex((s) => s.image === active.id);
      const newIndex = items.findIndex((s) => s.image === over.id);
      if (oldIndex === -1 || newIndex === -1) return d;
      return { ...d, slides: arrayMove(items, oldIndex, newIndex) };
    });
  };

  // Save ----------------------------------------------------------------
  const save = async () => {
    if (!draft || !activeSlug) return;
    setSaving(true);
    try {
      const payload = {
        label: draft.label,
        layout: draft.layout,
        icon: draft.icon,
        kicker: draft.kicker,
        title_top: draft.title_top,
        title_bottom: draft.title_bottom,
        subtitle: draft.subtitle,
        press_quote: draft.press_quote,
        buttons: draft.buttons || [],
        pills: draft.pills || [],
        slides: draft.slides || [],
        autoplay_ms: Number(draft.autoplay_ms) || 0,
        show_search: !!draft.show_search,
        search_placeholder: draft.search_placeholder || '',
      };
      const res = await axios.put(
        `${API}/hero-content/${activeSlug}`,
        payload,
        { headers: authHeaders() }
      );
      const saved = res.data?.hero;
      setHeroes((prev) => prev.map((h) => (h.slug === activeSlug ? saved : h)));
      setDraft({ ...saved });
      toast.success('Hero saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 text-white flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="admin-hero-editor">
      {/* Header */}
      <div className="border-b border-stone-800 bg-stone-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
              data-testid="hero-editor-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <Layout className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-bold">Hero Section Editor</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSlug && (
              <Link
                to={`/${activeSlug === 'home' ? '' : activeSlug}`}
                target="_blank"
                className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm"
                data-testid="hero-editor-preview"
              >
                <Eye className="w-4 h-4" /> Preview
              </Link>
            )}
            <button
              onClick={save}
              disabled={saving || !draft}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50"
              data-testid="hero-editor-save"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
        {/* Page list */}
        <aside>
          <h2 className="text-xs uppercase tracking-widest text-stone-500 mb-2">Pages</h2>
          <div className="space-y-1">
            {heroes.map((h) => (
              <button
                key={h.slug}
                onClick={() => selectHero(h.slug)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeSlug === h.slug
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-stone-900 hover:bg-stone-800 text-stone-300'
                }`}
                data-testid={`hero-editor-select-${h.slug}`}
              >
                <div className="font-semibold">{h.label || h.slug}</div>
                <div className={`text-xs ${activeSlug === h.slug ? 'opacity-80' : 'text-stone-500'}`}>/{h.slug === 'home' ? '' : h.slug}</div>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor */}
        {draft ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Page label">
                <input
                  type="text"
                  value={draft.label || ''}
                  onChange={(e) => set('label', e.target.value)}
                  className={inputCls}
                  data-testid="hero-field-label"
                />
              </Field>
              <Field label="Layout">
                <select
                  value={draft.layout || 'centered'}
                  onChange={(e) => set('layout', e.target.value)}
                  className={inputCls}
                  data-testid="hero-field-layout"
                >
                  {LAYOUTS.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Icon (lucide name)">
                <input
                  type="text"
                  value={draft.icon || ''}
                  onChange={(e) => set('icon', e.target.value)}
                  placeholder="e.g. Crown, Sparkles, Headphones"
                  className={inputCls}
                  data-testid="hero-field-icon"
                />
              </Field>
              <Field label="Autoplay (ms, 0 = off)">
                <input
                  type="number"
                  value={draft.autoplay_ms ?? 0}
                  onChange={(e) => set('autoplay_ms', e.target.value)}
                  className={inputCls}
                  data-testid="hero-field-autoplay"
                />
              </Field>
            </div>

            <Field label="Kicker / eyebrow">
              <input
                type="text"
                value={draft.kicker || ''}
                onChange={(e) => set('kicker', e.target.value)}
                className={inputCls}
                data-testid="hero-field-kicker"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Title (top line)">
                <input
                  type="text"
                  value={draft.title_top || ''}
                  onChange={(e) => set('title_top', e.target.value)}
                  className={inputCls}
                  data-testid="hero-field-title-top"
                />
              </Field>
              <Field label="Title (bottom line, accent)">
                <input
                  type="text"
                  value={draft.title_bottom || ''}
                  onChange={(e) => set('title_bottom', e.target.value)}
                  className={inputCls}
                  data-testid="hero-field-title-bottom"
                />
              </Field>
            </div>

            <Field label="Subtitle">
              <textarea
                rows={3}
                value={draft.subtitle || ''}
                onChange={(e) => set('subtitle', e.target.value)}
                className={inputCls}
                data-testid="hero-field-subtitle"
              />
            </Field>

            <Field label="Press quote">
              <input
                type="text"
                value={draft.press_quote || ''}
                onChange={(e) => set('press_quote', e.target.value)}
                className={inputCls}
                data-testid="hero-field-quote"
              />
            </Field>

            {/* Buttons */}
            <Section
              title="Buttons"
              action={
                <button
                  onClick={addButton}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm"
                  data-testid="hero-button-add"
                >
                  <Plus className="w-4 h-4" /> Add Button
                </button>
              }
            >
              {(draft.buttons || []).length === 0 ? (
                <Empty text="No buttons." />
              ) : (
                <div className="space-y-2">
                  {(draft.buttons || []).map((b, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-[2fr_2fr_auto] gap-2 bg-stone-900 border border-stone-800 rounded-lg p-2">
                      <input
                        type="text"
                        value={b.label}
                        onChange={(e) => updateButton(i, 'label', e.target.value)}
                        placeholder="Label"
                        className={inputCls}
                        data-testid={`hero-button-label-${i}`}
                      />
                      <input
                        type="text"
                        value={b.href}
                        onChange={(e) => updateButton(i, 'href', e.target.value)}
                        placeholder="/path or https://..."
                        className={inputCls}
                        data-testid={`hero-button-href-${i}`}
                      />
                      <button
                        onClick={() => removeButton(i)}
                        className="inline-flex items-center justify-center p-2 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded"
                        data-testid={`hero-button-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Pills */}
            <Section
              title="Feature pills"
              action={
                <button
                  onClick={addPill}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm"
                  data-testid="hero-pill-add"
                >
                  <Plus className="w-4 h-4" /> Add Pill
                </button>
              }
            >
              {(draft.pills || []).length === 0 ? (
                <Empty text="No pills." />
              ) : (
                <div className="space-y-2">
                  {(draft.pills || []).map((p, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto] gap-2 bg-stone-900 border border-stone-800 rounded-lg p-2">
                      <input
                        type="text"
                        value={p}
                        onChange={(e) => updatePill(i, e.target.value)}
                        placeholder="e.g. Nationwide Shipping"
                        className={inputCls}
                        data-testid={`hero-pill-${i}`}
                      />
                      <button
                        onClick={() => removePill(i)}
                        className="inline-flex items-center justify-center p-2 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded"
                        data-testid={`hero-pill-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Slides */}
            <Section
              title="Background slides"
              action={
                <label className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm cursor-pointer">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading…' : 'Upload Slide'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={uploadSlide}
                    className="hidden"
                    data-testid="hero-slide-upload"
                    disabled={uploading}
                  />
                </label>
              }
            >
              {(draft.slides || []).length === 0 ? (
                <Empty text="No slides. The hero will render without a background slideshow." />
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSlidesDragEnd}>
                  <SortableContext items={(draft.slides || []).map((s) => s.image)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {(draft.slides || []).map((s, i) => (
                        <SortableSlideRow
                          key={s.image}
                          slide={s}
                          index={i}
                          onCaption={(v) => updateSlideCaption(i, v)}
                          onReplace={(file) => replaceSlide(i, file)}
                          onRemove={() => removeSlide(i)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </Section>
          </div>
        ) : (
          <div className="text-sm text-stone-400">Pick a page on the left to edit its hero.</div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  'w-full px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none text-sm';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-stone-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Section = ({ title, action, children }) => (
  <div className="border border-stone-800 rounded-xl p-4 bg-stone-900/40">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-300">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => (
  <div className="text-sm text-stone-500 italic">{text}</div>
);

function SortableSlideRow({ slide, index, onCaption, onReplace, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slide.image });
  const fileRef = React.useRef(null);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const pick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileRef.current?.click();
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[auto_120px_1fr_auto_auto] items-center gap-3 bg-stone-900 border border-stone-800 rounded-lg p-2"
      data-testid={`hero-slide-row-${index}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-2 bg-stone-800 hover:bg-stone-700 rounded cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-stone-400" />
      </button>
      <div className="relative aspect-video rounded overflow-hidden bg-stone-800 group">
        {slide.image ? (
          <img loading="lazy" decoding="async" src={slide.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-600">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
        <button
          onClick={pick}
          className="absolute inset-0 flex items-center justify-center gap-1 text-xs font-semibold text-white bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`hero-slide-replace-${index}`}
        >
          <Upload className="w-3 h-3" /> Replace
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onReplace(f);
          }}
        />
      </div>
      <input
        type="text"
        value={slide.caption || ''}
        onChange={(e) => onCaption(e.target.value)}
        placeholder="Optional caption"
        className={inputCls}
      />
      <button
        onClick={pick}
        className="inline-flex items-center justify-center p-2 bg-stone-800 hover:bg-stone-700 rounded text-stone-300"
        title="Replace image"
        data-testid={`hero-slide-replace-btn-${index}`}
      >
        <Upload className="w-4 h-4" />
      </button>
      <button
        onClick={onRemove}
        className="inline-flex items-center justify-center p-2 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
